/* eslint-disable max-lines */
import React, {ChangeEventHandler, ComponentProps, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormState} from "../../hooks/useFormState";
import useVisibility from "../../hooks/useVisibility";
import {useLazyQuery} from "@apollo/client";

import without from "lodash/without";
import {getMapByObjectProperty, getArrayWithoutCopiesByObjectProperty, parseEmails} from "../../utils";
import {QueryData, usersQuery} from "./UsersSelect.graphql";

import {Group, QueryGetUsersByMixedDataArgs, User} from "../../schema";

import Link, {DefaultProps as LinkDefaultProps} from "../../ui/link";
import Textarea from "../../ui/textarea";
import Scroll from "../../ui/scroll";
import Checkbox from "../../ui/checkbox";
import ListSelector, {ListSelectorDisplayType, useListSelectorDisplayType} from "../ListSelector";
import {InformationIcon} from "../../ui/icons";
import PopupPortal from "../../ui/popupportal";
import Widget from "../../ui/widget";
import Button from "../../ui/button";
import Modal, {ModalContent, ModalFooter, ModalHeader} from "../../ui/modal";
import Spinner from "../../ui/spinner";

import cn from "classnames";
import classes from "./UsersSelect.module.css";
import SortButton, {SortInput} from "../../ui/sortbutton";
import {AccountGroupSortField} from "../../scenes/account/groups";
import {PickedChallengeProject} from "./AssignModal";
import {UserOption} from "./UserOption";
import {ChallengeOption} from "../../ui/MultiSelectField";
import AdminText from "./AdminText";

export type PickedUser = Pick<User, "id" | "fullName" | "failReasons" | "name" | "email" | "challenges"> & {
  groups: Pick<Group, "id" | "name">[];
};

type PickedGroup = Pick<Group, "id" | "name"> & {
  users: Pick<User, "id">[];
};

type WithoutNewUsersProps = {
  createNewUsers?: false;
  onSubmit?: (usersIds: User["id"][], emails?: string[], adminText?: string) => void;
  onSelectChange?: (usersIds: User["id"][]) => void;
};
type WithNewUsersProps = {
  createNewUsers: true;
  onSubmit?: (usersIds: User["id"][], emails: string[], adminText?: string) => void;
  onSelectChange?: (usersIds: User["id"][], emails: string[]) => void;
};

export type Props = Omit<ComponentProps<"div">, "children" | "onSubmit"> & {
  users: PickedUser[];
  groups: PickedGroup[];
  currentItem?: PickedChallengeProject | undefined;
  showTabs?: TAB[];
  initialValues?: Partial<Pick<FormState, "usersIds" | "groupsIds">>;
  submitDisabled?: boolean;
  onSort: React.Dispatch<React.SetStateAction<SortInput<AccountGroupSortField>>>;
  defaultSort: SortInput<AccountGroupSortField>;
  sort: SortInput<AccountGroupSortField>;
  withExcludeGroups?: boolean;
  isGroupEdit?: boolean;
  challenges?: ChallengeOption[];
} & (WithoutNewUsersProps | WithNewUsersProps);

enum TAB {
  ListSelector = "listSelector",
  PlainTextInput = "textInput",
  Groups = "groupsSelector",
}

type FormState = {
  usersIds: User["id"][];
  groupsIds: Group["id"][];
  excludeGroupsIds: Group["id"][];
  plainText: string;
};

const horizontalMediaQuery = "(min-width: 640px)";

export default function UsersSelect({
  users,
  groups,
  currentItem,
  onSort,
  defaultSort,
  sort,
  initialValues,
  createNewUsers,
  onSubmit,
  onSelectChange,
  showTabs,
  submitDisabled,
  withExcludeGroups,
  isGroupEdit,
  className,
  challenges,
  ...props
}: Props) {
  const {t} = useTranslation();
  const [currentTab, setCurrentTab] = useState<TAB>(
    showTabs ? Object.values(TAB).find((tab) => showTabs.includes(tab))! : TAB.ListSelector,
  );

  const formState = useFormState<FormState>({
    initialValues: {
      usersIds: initialValues?.usersIds ?? [],
      groupsIds: initialValues?.groupsIds ?? [],
      excludeGroupsIds: [],
      plainText: "",
    },
    preventDefault: true,
  });

  const {usersIds, groupsIds, excludeGroupsIds, plainText} = formState.values;

  const emails = useMemo<string[]>(() => parseEmails(plainText, true), [plainText]);

  const onUsersSelect = useCallback(
    (selectedItemsIds: string[]) => {
      formState.setValues({usersIds: selectedItemsIds});
    },
    [formState],
  );

  const onGroupSelect = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const groupId = e.currentTarget.getAttribute("data-group-id")!;
      const checked = e.currentTarget.checked;
      if (groupId === "all") {
        formState.setValues(() => ({
          groupsIds: checked ? groups.map((group) => group.id) : [],
          excludeGroupsIds: [],
        }));
      } else {
        formState.setValues((values) => {
          let excludeGroupsIds: string[] = [];
          if (withExcludeGroups) {
            let toExclude = values.groupsIds.includes(groupId);
            excludeGroupsIds = toExclude
              ? values.excludeGroupsIds.concat(groupId)
              : without(values.excludeGroupsIds, groupId);
          }

          return {
            groupsIds: checked ? values.groupsIds.concat(groupId) : without(values.groupsIds, groupId),
            excludeGroupsIds: excludeGroupsIds,
          };
        });
      }
    },
    [formState, groups, withExcludeGroups],
  );

  const onTextInput = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => {
      formState.setValues({plainText: e.currentTarget.value});
    },
    [formState],
  );

  const mappedUsersById = useMemo(() => getMapByObjectProperty(users, "id"), [users]);
  const mappedGroupsById = useMemo(() => getMapByObjectProperty(groups, "id"), [groups]);
  const mappedUsersByEmail = useMemo(() => getMapByObjectProperty(users, "email"), [users]);

  const usersSelected = useMemo(
    () => usersIds.map((userId) => mappedUsersById.get(userId)!),
    [usersIds, mappedUsersById],
  );

  const selectedGroups = useMemo(
    () => groupsIds.map((groupId) => mappedGroupsById.get(groupId)!),
    [groupsIds, mappedGroupsById],
  );
  const excludeGroups = useMemo(
    () => excludeGroupsIds.map((groupId) => mappedGroupsById.get(groupId)!),
    [excludeGroupsIds, mappedGroupsById],
  );
  const usersFromGroups = useMemo(() => {
    let selectedUsers = selectedGroups.flatMap((group) => group.users).map(({id}) => mappedUsersById.get(id)!);
    let excludedUsers = excludeGroups.flatMap((group) => group.users).map(({id}) => mappedUsersById.get(id)!);
    return without(selectedUsers, ...excludedUsers);
  }, [selectedGroups, excludeGroups, mappedUsersById]);

  const [usersSelectedByPlainText, usersNotFoundEmais] = useMemo(
    () =>
      emails.reduce<[PickedUser[], string[]]>(
        ([found, notFound], email) => {
          const user = mappedUsersByEmail.get(email);
          if (user) {
            return [found.concat(user), notFound];
          } else {
            return [found, notFound.concat(email)];
          }
        },
        [[], []],
      ),
    [emails, mappedUsersByEmail],
  );

  const usersNotFoundEmaisFiltered = useMemo(() => Array.from(new Set(usersNotFoundEmais)), [usersNotFoundEmais]);

  const allSelectedUsers = useMemo(() => {
    const allSelectedUsers = usersSelected.concat(usersFromGroups, usersSelectedByPlainText ?? []);
    return getArrayWithoutCopiesByObjectProperty(allSelectedUsers, "id");
  }, [usersSelected, usersFromGroups, usersSelectedByPlainText]);

  useEffect(() => {
    if (!onSelectChange) {
      return;
    }
    const ids = getIds(allSelectedUsers);
    if (createNewUsers) {
      (onSelectChange as WithNewUsersProps["onSelectChange"])!(ids, usersNotFoundEmais);
    } else {
      (onSelectChange as WithoutNewUsersProps["onSelectChange"])!(ids);
    }
  }, [allSelectedUsers, usersNotFoundEmais, createNewUsers, onSelectChange]);

  const [query, {data}] = useLazyQuery<QueryData, QueryGetUsersByMixedDataArgs>(usersQuery, {
    notifyOnNetworkStatusChange: true,
  });

  const {users: confirmedUsers, emails: confirmedEmails} = data?.mixed ?? {};

  const [confirmationScreenVisible, showConfirmationScreen, hideConfirmationScreen] = useVisibility();

  const selectedChallengesIds = useMemo(() => {
    return challenges
      ?.filter((challenge) => challenge.value)
      .map((l) => l.value)
      .filter(Boolean) as string[];
  }, [challenges]);

  const onSubmitButtonClick = useCallback(() => {
    const variables = {
      ...formState.values,
      challengeIds: selectedChallengesIds as string[],
    };
    query({variables});
    showConfirmationScreen();
  }, [formState.values, query, selectedChallengesIds, showConfirmationScreen]);

  const [usersListVisible, showUsersList, hideUsersList] = useVisibility();

  const disableUsersListButton = allSelectedUsers.length === 0 && usersNotFoundEmais.length === 0;

  const showCtrlShiftHint = useListSelectorDisplayType(horizontalMediaQuery) === ListSelectorDisplayType.HORIZONTAL;

  const stopPropagationHandler = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div className={cn(classes.root, className)} {...props}>
      <div className={classes.tabsWrapper}>
        <Tab value={TAB.ListSelector} currentTab={currentTab} setCurrentTab={setCurrentTab}>
          {t("components.UsersSelect.listSelector.tabName")}
        </Tab>
        <Tab value={TAB.Groups} currentTab={currentTab} setCurrentTab={setCurrentTab}>
          {t("components.UsersSelect.groupsSelector.tabName")}
        </Tab>
        <Tab value={TAB.PlainTextInput} currentTab={currentTab} setCurrentTab={setCurrentTab}>
          {t("components.UsersSelect.plainText.tabName")}
        </Tab>
      </div>

      {currentTab === TAB.ListSelector && (
        <>
          <ListSelector<Pick<User, "id" | "fullName">>
            items={users}
            nameField="fullName"
            onChange={onUsersSelect}
            horizontalMediaQuery={horizontalMediaQuery}
            restTitle={t("components.UsersSelect.allUsers")}
            pickedTitle={t("components.UsersSelect.selectedUsers")}
            selectedItemsIds={formState.values.usersIds}
          />
          {showCtrlShiftHint && (
            <span className={classes.smallPrint}>
              <InformationIcon />
              {t("components.UsersSelect.useCtrlShift")}
            </span>
          )}
        </>
      )}

      {currentTab === TAB.Groups && (
        <>
          <span className={classes.helpText}>
            {t("components.UsersSelect.groupsSelector.helpText")}{" "}
            {withExcludeGroups && t("components.UsersSelect.groupsSelector.helpTextExcludeGroups")}
          </span>
          <div className={classes.withSort}>
            <p className={classes.nameSortText}>{t("components.UsersSelect.nameSortText")}</p>
            <SortButton
              field={AccountGroupSortField.NAME}
              onSetSort={onSort}
              defaultSort={defaultSort}
              selectedSort={sort}
            />
          </div>
          <label className={classes.checkboxLabel}>
            <Checkbox
              checked={formState.values.groupsIds.length === groups.length}
              indeterminate={
                formState.values.groupsIds.length !== 0 && formState.values.groupsIds.length !== groups.length
              }
              onChange={onGroupSelect}
              data-group-id={"all"}
            />
            <span className={classes.checkboxText}>{t("components.UsersSelect.groupsSelector.checkAllBoxes")}</span>
          </label>
          <Scroll className={classes.groupsSelect}>
            {groups.map(({id, name}) => (
              <label key={id} className={classes.checkboxLabel}>
                <Checkbox
                  checked={formState.values.groupsIds.includes(id)}
                  crossed={formState.values.excludeGroupsIds.includes(id)}
                  onChange={onGroupSelect}
                  data-group-id={id}
                />
                <span className={classes.checkboxText}>{name}</span>
              </label>
            ))}
          </Scroll>
        </>
      )}

      {currentTab === TAB.PlainTextInput && (
        <>
          <span className={classes.helpText}>{t("components.UsersSelect.plainText.helpText")}</span>
          <Textarea
            className={classes.plainText}
            name="plainText"
            rows={15}
            required
            placeholder={t("components.UsersSelect.plainText.placeholder")}
            value={formState.values.plainText}
            onChange={onTextInput}
          />
        </>
      )}

      <SelectionSummary
        users={allSelectedUsers}
        selectedEmails={usersNotFoundEmaisFiltered}
        createNewUsers={createNewUsers}
      />

      <Link type="button" className={classes.showListLink} onClick={showUsersList} disabled={disableUsersListButton}>
        {t("components.UsersSelect.showSelectedUsersList")}
      </Link>
      <span className={classes.smallPrint}>
        <InformationIcon />
        {t("components.UsersSelect.summaryPreview")}
      </span>

      <div className={classes.bottom}>
        <Button
          onClick={onSubmitButtonClick}
          type="button"
          color="success"
          disabledColor="secondary"
          disabled={submitDisabled}
        >
          {t("components.UsersSelect.continue")}
        </Button>
      </div>

      {usersListVisible && (
        <PopupPortal as="div" onMouseDown={stopPropagationHandler} center>
          <Widget className={classes.usersListWidget} showCloseButton onCloseButtonClick={hideUsersList}>
            <Widget.Title>{t("components.UsersSelect.usersList.title")}</Widget.Title>
            <UsersList users={allSelectedUsers} emails={usersNotFoundEmaisFiltered} createNewUsers={createNewUsers} />
          </Widget>
        </PopupPortal>
      )}

      {confirmationScreenVisible && (
        <ConfirmationModal
          users={confirmedUsers}
          emails={confirmedEmails ?? undefined}
          onClose={hideConfirmationScreen}
          onSubmit={onSubmit}
          createNewUsers={createNewUsers}
          isGroupEdit={isGroupEdit}
          challenges={challenges}
        />
      )}
    </div>
  );
}

type TabProps = Omit<LinkDefaultProps, "className" | "disabled" | "onClick"> & {
  value: TAB;
  currentTab: TAB;
  setCurrentTab: (tab: TAB) => void;
};

function Tab({value, currentTab, setCurrentTab, ...props}: TabProps) {
  return (
    <Link
      type="button"
      className={classes.tab}
      disabled={currentTab === value}
      onClick={() => setCurrentTab(value)}
      {...props}
    />
  );
}

type SelectionSummaryProps = Omit<ComponentProps<"span">, "children"> & {
  users: any[];
  selectedEmails?: string[];
  totalEmails?: string[];
  createNewUsers?: boolean;
  isGroupEdit?: boolean;
  isConfirmation?: boolean;
  participatedUsers?: UsersListProps["users"];
  inactiveUsers?: UsersListProps["users"];
  usersWithErrors?: PickedUser[];
  usersToSelect?: PickedUser[];
};

function SelectionSummary({
  users,
  usersWithErrors,
  usersToSelect,
  selectedEmails,
  totalEmails,
  createNewUsers,
  inactiveUsers,
  participatedUsers,
  isGroupEdit,
  isConfirmation,
  className,
  ...props
}: SelectionSummaryProps) {
  const {t} = useTranslation();

  const usersWithErrorCounter = useMemo(() => {
    return usersWithErrors?.length ? usersWithErrors?.length : 0;
  }, [usersWithErrors]);

  const selectedUsersCounter = useMemo(() => {
    return (
      (users.length ? users.filter((user) => !user.failReasons || user.failReasons.length === 0).length : 0) +
      (selectedEmails && !isGroupEdit && isConfirmation ? selectedEmails?.length : 0)
    );
  }, [users, isGroupEdit, selectedEmails, isConfirmation]);

  const allUsersCounter = useMemo(() => {
    return (
      (usersWithErrors ? usersWithErrors?.length : 0) +
      (usersToSelect ? usersToSelect?.length : 0) +
      (totalEmails && !isGroupEdit ? totalEmails?.length : 0)
    );
  }, [totalEmails, usersToSelect, usersWithErrors, isGroupEdit]);

  return (
    <span className={cn(classes.summaryPreview, className)} {...props}>
      {!isConfirmation ? (
        <span>
          {t("components.UsersSelect.summary.usersSelected", {
            selected: selectedUsersCounter,
          })}
        </span>
      ) : null}

      {isConfirmation && !isGroupEdit ? (
        <span>
          {t("components.UsersSelect.summary.allUsersAssign", {
            selected: selectedUsersCounter,
            all: allUsersCounter,
          })}
        </span>
      ) : null}

      {isConfirmation && isGroupEdit ? (
        <span>
          {t("components.UsersSelect.summary.allUsersGroupEdit", {
            selected: selectedUsersCounter,
            all: allUsersCounter,
          })}
        </span>
      ) : null}

      {usersWithErrorCounter ? (
        <span>
          {t("components.UsersSelect.summary.usersWithError", {
            ignored: usersWithErrorCounter,
          })}
        </span>
      ) : null}

      {isGroupEdit && <span>{t("components.UsersSelect.summary.deactivatedNote")}</span>}
      {selectedEmails &&
        selectedEmails.length > 0 &&
        (createNewUsers ? (
          <span>
            {t("components.UsersSelect.summary.newUsers", {
              newUsersCount: selectedEmails.length,
            })}
          </span>
        ) : (
          <span>
            {t("components.UsersSelect.summary.usersNotFound", {
              notFoundCount: selectedEmails.length,
            })}
          </span>
        ))}
    </span>
  );
}

type ConfirmationModalProps = Omit<ComponentProps<typeof Modal>, "children" | "onSubmit"> &
  Pick<WithoutNewUsersProps | WithNewUsersProps, "createNewUsers" | "onSubmit"> & {
    users?: UsersListProps["users"];
    emails?: UsersListProps["emails"];
    participatedUsers?: UsersListProps["users"];
    inactiveUsers?: UsersListProps["users"];
    isGroupEdit?: boolean;
    challenges?: ChallengeOption[];
  };

function ConfirmationModal({
  users,
  emails,
  participatedUsers,
  inactiveUsers,
  createNewUsers,
  isGroupEdit,
  onSubmit,
  ...props
}: ConfirmationModalProps) {
  const {t} = useTranslation();

  const [selectedUsersIds, setSelectedUsersIds] = useState<User["id"][]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [adminText, setAdminText] = useState<string>("");

  const [usersWithErrors, usersToSelect] = useMemo(() => {
    if (!users) return [];
    return [
      users.filter((user) => user.failReasons && user.failReasons.some((reason) => reason !== "PARTLY_ASSIGNED")),
      users.filter(
        (user) =>
          !user.failReasons ||
          user.failReasons.length === 0 ||
          (user.failReasons.length === 1 && user.failReasons[0] === "PARTLY_ASSIGNED"),
      ),
    ];
  }, [users]);

  useEffect(() => {
    if (usersToSelect) {
      setSelectedUsersIds(getIds(usersToSelect));
    }
  }, [usersToSelect]);

  useEffect(() => {
    if (emails) {
      setSelectedEmails(emails);
    }
  }, [emails]);

  const onSubmitButtonClick = useCallback(() => {
    if (createNewUsers) {
      (onSubmit as WithNewUsersProps["onSubmit"])!(selectedUsersIds, emails ?? [], adminText);
    } else {
      (onSubmit as WithoutNewUsersProps["onSubmit"])!(selectedUsersIds, emails, adminText);
    }
  }, [createNewUsers, onSubmit, selectedUsersIds, emails, adminText]);

  const onUserSelect = useCallback<NonNullable<UsersListProps["onUserSelect"]>>((userId, checked) => {
    setSelectedUsersIds((ids) => (checked ? ids.concat(userId) : without(ids, userId)));
  }, []);

  const onEmailSelect = useCallback<NonNullable<UsersListProps["onEmailSelect"]>>((email, checked) => {
    setSelectedEmails((emails) => (checked ? emails.concat(email) : without(emails, email)));
  }, []);

  const stopPropagationHandler = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const totalSelected = useMemo(() => {
    return isGroupEdit ? selectedUsersIds.length : selectedUsersIds.length + selectedEmails.length;
  }, [selectedEmails, selectedUsersIds, isGroupEdit]);

  return (
    <PopupPortal onMouseDown={stopPropagationHandler} center>
      <Modal size="l" {...props}>
        <ModalHeader>
          <span>{t("components.UsersSelect.ConfirmationModal.title")}</span>
        </ModalHeader>
        <ModalContent>
          {users ? (
            <>
              <span>{t("components.UsersSelect.ConfirmationModal.helpText")}</span>
              <AdminText value={adminText} onChange={setAdminText} />

              <SelectionSummary
                createNewUsers={createNewUsers}
                users={selectedUsersIds}
                usersWithErrors={usersWithErrors}
                usersToSelect={usersToSelect}
                selectedEmails={selectedEmails}
                totalEmails={emails}
                participatedUsers={participatedUsers}
                inactiveUsers={inactiveUsers}
                isGroupEdit={isGroupEdit}
                isConfirmation
              />
              <UsersList
                withCheckboxes
                createNewUsers={createNewUsers}
                usersWithErrors={usersWithErrors}
                usersToSelect={usersToSelect}
                users={users}
                emails={emails}
                selectedUsersIds={selectedUsersIds}
                onUserSelect={onUserSelect}
                selectedEmails={selectedEmails}
                onEmailSelect={onEmailSelect}
              />
            </>
          ) : (
            <div className={classes.spinnerWrapper}>
              <Spinner className={classes.spinner} />
            </div>
          )}
        </ModalContent>
        <ModalFooter className={classes.confirmationModalFooter}>
          <Button
            type="button"
            color="success"
            disabledColor="secondary"
            disabled={totalSelected === 0}
            onClick={onSubmitButtonClick}
          >
            {t("components.UsersSelect.ConfirmationModal.submit")}
          </Button>
        </ModalFooter>
      </Modal>
    </PopupPortal>
  );
}

type UsersListProps = Omit<ComponentProps<"div">, "children"> & {
  users?: PickedUser[];
  emails?: string[];
  usersWithErrors?: PickedUser[];
  usersToSelect?: PickedUser[];
  createNewUsers?: boolean;
  withCheckboxes?: boolean;

  selectedUsersIds?: User["id"][];
  onUserSelect?: (changedId: User["id"], newValue: boolean) => void;

  selectedEmails?: string[];
  onEmailSelect?: (changedEmail: string, newValue: boolean) => void;
};

export function UsersList({
  users,
  usersWithErrors,
  usersToSelect,
  emails,
  createNewUsers,
  withCheckboxes,
  selectedUsersIds = [],
  onUserSelect: exOnUserSelect,
  selectedEmails = [],
  onEmailSelect: exOnEmailSelect,
  className,
  ...props
}: UsersListProps) {
  const {t} = useTranslation();

  const onUserSelect = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const userId = e.currentTarget.getAttribute("data-id")!;
      const checked = e.currentTarget.checked;
      exOnUserSelect && exOnUserSelect(userId, checked);
    },
    [exOnUserSelect],
  );

  const onEmailSelect = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const email = e.currentTarget.getAttribute("data-id")!;
      const checked = e.currentTarget.checked;
      exOnEmailSelect && exOnEmailSelect(email, checked);
    },
    [exOnEmailSelect],
  );

  return (
    <Scroll className={cn(classes.usersListWrapper, className)} {...props}>
      {!usersWithErrors &&
        users?.map((user) => (
          <UserOption
            key={user.id}
            user={user}
            showCheckbox={withCheckboxes}
            forNewUsers={createNewUsers}
            onSelect={onEmailSelect}
            disabled
            forShow
          />
        ))}

      {emails && emails.length > 0 && (
        <>
          <span className={classes.usersListHeader}>
            {createNewUsers
              ? t("components.UsersSelect.usersList.newUsers")
              : t("components.UsersSelect.usersList.notFound")}
          </span>
          {emails.map((email) => (
            <UserOption
              key={email}
              email={email}
              showCheckbox={withCheckboxes}
              forNewUsers={createNewUsers}
              checked={selectedEmails?.includes(email)}
              onSelect={onEmailSelect}
            />
          ))}
        </>
      )}

      {usersWithErrors?.length ? (
        <span className={classes.usersListHeader}>{t("components.UsersSelect.usersList.usersWithErrors")}:</span>
      ) : null}
      {usersWithErrors?.map((user) => (
        <UserOption
          key={user.email}
          user={user}
          showCheckbox={withCheckboxes}
          checked={user.failReasons?.length === 0 ? selectedUsersIds.includes(user.id) : false}
          disabled={user.failReasons && user.failReasons?.length !== 0 ? true : false}
          onSelect={onUserSelect}
        />
      ))}

      {usersToSelect?.length ? (
        <span className={classes.usersListHeader}>{t("components.UsersSelect.usersList.selectedUsers")}:</span>
      ) : null}
      {usersToSelect?.map((user) => (
        <UserOption
          key={user.email}
          user={user}
          showCheckbox={withCheckboxes}
          checked={selectedUsersIds.includes(user.id)}
          onSelect={onUserSelect}
        />
      ))}
    </Scroll>
  );
}

function getIds(arrayOfObjects: {id: string}[]) {
  return arrayOfObjects.map(({id}) => id);
}
